import React, { useMemo, useState } from 'react';
import { Card, Empty } from 'antd';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { useNotifications } from '../hooks/useNotifications';
import { TimeRangeLinks } from '../ui/TimeRangeLinks';


type Period = 'day' | 'week' | 'month' | 'year' | 'all';
const D = 24 * 60 * 60 * 1000;

const STATUS_META = [
  { key: 'new',        label: 'Новые',      color: '#ff4d4f' },
  { key: 'processing', label: 'В работе',   color: '#faad14' },
  { key: 'ready',      label: 'Готовы',     color: '#1677ff' },
  { key: 'shipped',    label: 'Отгружены',  color: '#13c2c2' },
  { key: 'canceled',   label: 'Отменены',   color: '#ff7a45' },
  { key: 'returned',   label: 'Возвраты',   color: '#9254de' },
] as const;

const PERIOD_LABEL: Record<Period, string> = {
  day: '(24 ч)', week: '(7 дн)', month: '(30 дн)', year: '(365 дн)', all: '(за всё время)',
};

type Props = {
  height?: number;
  rangeDays?: number;                 // для обратной совместимости
  // управляемый режим:
  period?: Period;
  onPeriodChange?: (p: Period) => void;
  // уже отфильтрованные заказы (как в таблице)
  source?: any[];
};

const toPeriod = (d?: number): Period => {
  switch (d) {
    case 1: return 'day';
    case 7: return 'week';
    case 30: return 'month';
    case 365: return 'year';
    default: return 'month';
  }
};

const rangeFor = (p: Period) => {
  const now = Date.now();
  switch (p) {
    case 'day':  return { from: now - 1 * D, to: now };
    case 'week': return { from: now - 7 * D, to: now };
    case 'month':return { from: now - 30 * D, to: now };
    case 'year': return { from: now - 365 * D, to: now };
    case 'all':  return { from: 0, to: now };
  }
};

const fmtPct = (f: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(f * 100) + '%';

const OrdersSplitDonut: React.FC<Props> = ({ height = 420, rangeDays, period, onPeriodChange, source }) => {
  const hook = useNotifications(10_000);
  const controlled = period !== undefined;

  const [innerPeriod, setInnerPeriod] = useState<Period>(toPeriod(rangeDays));
  const p = controlled ? (period as Period) : innerPeriod;
  const setP = (val: Period) => (controlled ? onPeriodChange?.(val) : setInnerPeriod(val));

  // Если пришёл source — используем его (он уже отфильтрован так же, как таблица)
  const items = source ?? hook.items;

  const { from, to } = rangeFor(p);
  const { data, total } = useMemo(() => {
    // группируем по статусам — считаем КОЛИЧЕСТВО ТОВАРНЫХ ПОЗИЦИЙ (а не заказов!)
    const bucket: Record<string, number> = {};

    for (const o of items ?? []) {
      const created = new Date((o as any).createdAt).getTime();
      if (!Number.isFinite(created) || !(created >= from && created < to)) continue; // [from, to)

      const st = String((o as any).status ?? 'unknown');

      // позиции заказа
      const lines: any[] = Array.isArray((o as any).items) ? (o as any).items : [];

      // считаем число строк-товаров (как в таблице). Если в заказе нет позиций — считаем 1 строку.
      const linesCount = lines.length || 1;

      // Если когда-нибудь понадобится суммировать количества:
      // const linesCount = lines.reduce((s,it)=>s+Number(it.quantity ?? it.qty ?? 1),0) || 1;

      bucket[st] = (bucket[st] || 0) + linesCount;
    }

    const rows = STATUS_META.map(s => ({
      key: s.key, name: s.label, value: bucket[s.key as string] || 0, color: s.color,
    }));

    const total = rows.reduce((a, r) => a + r.value, 0);
    const filtered = rows.filter(r => r.value > 0);
    const out = { data: filtered.length ? filtered : rows, total };

    if (process.env.NODE_ENV !== 'production') {
      const segmentsSum = rows.reduce((a, r) => a + r.value, 0);
      if (Math.abs(segmentsSum - total) > 0.001) {
        // eslint-disable-next-line no-console
        console.warn('OrdersSplitDonut: сумма сегментов != total', { total, segmentsSum });
      }
    }

    return out;
  }, [items, from, to ]);


  const Tip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const pct = total ? fmtPct(p.value / total) : '0%';
    return (
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '8px 10px', boxShadow: '0 4px 16px rgba(0,0,0,.06)' }}>
        <div style={{ fontWeight: 600 }}>{p.name}</div>
        <div>{p.value} шт · {pct}</div>
      </div>
    );
  };

  return (
    <Card
      className="stat-card stat-card--accent-blue"
      styles={{ header: { paddingTop: 10, paddingBottom: 8, alignItems: 'center' } }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="stat-card__icon" style={{ display: 'grid', placeItems: 'center' }}>
            <ShoppingCartOutlined />
          </span>
          <span className="stat-card__title">Структура заказов</span>
          <span className="stat-card__suffix">{PERIOD_LABEL[p]}</span>
        </div>
      }
      extra={<TimeRangeLinks value={p} onChange={setP} withAll />}
    >
      <div style={{ width: '100%', height }}>
        {total === 0 ? (
          <Empty description="Нет данных" />
        ) : (
          <ResponsiveContainer>
            <PieChart>
              <Tooltip content={<Tip />} />
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                innerRadius={110}
                outerRadius={170}
                labelLine
                label={({ name, value }) => (value ? `${name}: ${fmtPct(total ? Number(value) / total : 0)}` : '')}
              >
                {data.map((r, i) => <Cell key={i} fill={r.color} />)}
              </Pie>

              {/* центр: всего ТОВАРОВ (строк таблицы) */}
              <text x="50%" y="50%" dy={-10} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 12, fill: '#667085' }}>
                Всего товаров
              </text>
              <text x="50%" y="50%" dy={12} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 18, fontWeight: 700 }}>
                {total} шт
              </text>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default OrdersSplitDonut;
